import { z } from "zod";

import { unlistedDiscriminantSchema } from "../../../shared/core/discriminated-union-read.schema";

// ============================================================
// Request the retirement of a tenant's org-wide git push token
// ============================================================

/**
 * Record ONE operator instruction to retire one tenant's org-wide Forgejo push
 * token (`vibe-push`): the agent deletes it on the tenant's git host, proves it
 * gone by name and by value, and drops its Vault copy. Deleting the Forgejo row
 * revokes every copy of the token that ever left the tenant — irreversible for
 * anyone still holding one.
 *
 * Off on every tenant until an operator records it here. It is delivered to the
 * agent on its next desired-state poll, and the agent's status report echoes the
 * directive back once the token is gone.
 */
export const AdminVibeTenantClusterRequestOrgGitCredentialRetirementBodySchema = z
  .object({
    organizationId: z.string().min(1),
    /** Why. Required: it is the operator's stated reason on the admin audit row. */
    reason: z.string().min(1).max(500)
  })
  .strict();

/**
 * Discriminated outcome. Every variant is a true answer; only a persistence
 * fault is an error.
 *
 *   - requested: an instruction was recorded under `directiveId`. On a tenant
 *     already retired whose agent's admin-token census shows the token GREW
 *     BACK, the stamped instruction is re-issued under a fresh directive, and
 *     `supersededDirectiveId` names the one it replaced; null on a first ask.
 *   - already_requested: an instruction is outstanding — the same one is
 *     returned and nothing new is minted.
 *   - already_retired: the agent reported the token retired under
 *     `directiveId`, and its census does not show it back. Nothing was written.
 *   - operational_token_missing: refused. The agent's latest fresh census of the
 *     site admin's tokens, taken at `observedAt`, does not list its own
 *     operational token (`vibe-agent`) — so retiring the org token would leave
 *     the admin holding none, and the agent nothing to drive its git host with.
 *     `adminTokenNames` is that census. Nothing was written; ask again once the
 *     agent reports the operational token back.
 *   - not_found: no cluster for that organization.
 */
export const AdminVibeTenantClusterRequestOrgGitCredentialRetirementOutcomeSchema =
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("requested"),
      directiveId: z.string().uuid(),
      requestedAt: z.string().datetime(),
      supersededDirectiveId: z.string().uuid().nullable()
    }),
    z.object({
      kind: z.literal("already_requested"),
      directiveId: z.string().uuid(),
      requestedAt: z.string().datetime()
    }),
    z.object({
      kind: z.literal("already_retired"),
      directiveId: z.string().uuid(),
      retiredAt: z.string().datetime()
    }),
    z.object({
      kind: z.literal("operational_token_missing"),
      observedAt: z.string().datetime(),
      adminTokenNames: z.array(z.string()).max(100)
    }),
    z.object({ kind: z.literal("not_found") })
  ]);
export type AdminVibeTenantClusterRequestOrgGitCredentialRetirementOutcome = z.infer<
  typeof AdminVibeTenantClusterRequestOrgGitCredentialRetirementOutcomeSchema
>;

/** The outcome as a reader parses it: a `kind` a newer backend added reads as the server's own word. */
export const AdminVibeTenantClusterRequestOrgGitCredentialRetirementOutcomeReadSchema = z.union([
  AdminVibeTenantClusterRequestOrgGitCredentialRetirementOutcomeSchema,
  z.looseObject({
    kind: unlistedDiscriminantSchema(
      AdminVibeTenantClusterRequestOrgGitCredentialRetirementOutcomeSchema
    )
  })
]);
